import axios from 'axios';
import React, { useState, useEffect } from 'react';
import IndexPage_Navbar from '../components/Header/headerOfUser.js';
import {
  Grid,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  Box,
  useMediaQuery,
  useTheme,
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { GroupAdd, School, HelpOutline, Close } from '@mui/icons-material';
import io from 'socket.io-client';
import ActivityCard from '../components/ActivityCard';
import url from '../url.js';
import '../styles/Index.css';
import { getActivityRequestConfig } from '../utils/activityRequestConfig';

export default function Index() {
  const [sortOrder, setSortOrder] = useState('new_to_old');
  const [all, setAll] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', content: '' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (event) => {
    setSortOrder(event.target.value);
  };

  const connectWebSocket = () => {
    setWs(io(url.backendHost));
  };

  const handleDialogOpen = (title, content) => {
    setDialogContent({ title, content });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    const getActivities = async () => {
      try {
        setLoading(true);
        const fetchData = await axios.get(
          `${url.backendHost}api/activities/myJoined/${localStorage.getItem('userId')}`,
          getActivityRequestConfig()
        );
        setActivities(fetchData.data);
        console.log("fetchData: ", fetchData.data)
      } catch (err) {
        // 
      } finally {
        setLoading(false);
      }
    };
    getActivities();

    if (ws) {
      initWebSocket();
    }
  }, [ws]);

  const initWebSocket = () => {
    ws.on('connect', () => {
      // 
    });

    ws.on('event02', (arg, callback) => {
      // 
      callback({
        status: 'event02 ok',
      });
    });
  };

  const sortedActivities = [...activities].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'new_to_old' ? dateB - dateA : dateA - dateB;
  });

  // 空狀態組件
  const EmptyStateComponent = () => (
    <Paper 
      elevation={2}
      sx={{ 
        padding: 4,
        textAlign: 'center',
        backgroundColor: 'var(--surface-color)',
        borderRadius: 2,
        border: '2px dashed #dee2e6',
        margin: '20px 0'
      }}
    >
      <School sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
      <Typography variant="h5" component="h2" sx={{ 
        fontWeight: 500,
        color: '#495057',
        mb: 2
      }}>
        還沒有參與任何探究活動
      </Typography>
      <Typography variant="body1" sx={{ 
        color: '#6c757d',
        mb: 4,
        maxWidth: 500,
        margin: '0 auto 32px auto'
      }}>
        向您的老師詢問活動代碼，或等待老師邀請您參與探究活動。一旦加入活動，您就可以與同學們一起進行協作學習和知識建構。
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<GroupAdd />}
          sx={{
            backgroundColor: 'var(--secondary-color)',
            color: 'var(--white)',
            '&:hover': {
              backgroundColor: 'var(--secondary-hover)'
            },
            borderRadius: '50px',
            padding: '12px 28px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '16px',
            boxShadow: '0 2px 8px rgba(236, 198, 250, 0.4)',
            '&:hover': {
              backgroundColor: 'var(--secondary-hover)',
              boxShadow: '0 4px 12px rgba(221, 181, 240, 0.6)',
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
          onClick={() => handleDialogOpen(
            '如何加入探究活動',
            '有兩種方式可以加入探究活動：\n\n1. 📝 詢問老師獲取活動代碼\n   請向您的老師索取6位數的活動代碼，然後在首頁點擊「探索更多活動」按鈕輸入代碼。\n\n2. 創建活動： 點擊首頁上的「我創建的活動」，即可至創建活動頁面，創建活動並邀請其他同學加入吧！'
          )}
        >
          如何加入活動
        </Button>
        <Button
          variant="outlined"
          startIcon={<HelpOutline />}
          sx={{
            borderColor: '#A0B9FF',
            color: '#333333',
            '&:hover': {
              borderColor: '#8AA5FF',
              backgroundColor: 'var(--surface-color)'
            },
            borderRadius: '50px',
            padding: '12px 28px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '16px',
            transition: 'all 0.2s ease-in-out'
          }}
          onClick={() => handleDialogOpen(
            '什麼是探究活動',
            '探究活動是一種協作學習的方式，讓您能夠：\n\n🧠 建立知識節點\n   創建包含想法、資訊、提問等不同類型的學習節點\n\n👥 與同學協作\n   在小組中與同學分享觀點、討論問題\n\n🔗 連結想法\n   建立節點之間的關聯，形成知識網絡\n\n📊 追蹤學習進度\n   通過儀表板查看學習統計和活動分析\n\n💭 深度思考\n   透過結構化的討論促進批判性思維'
          )}
        >
          什麼是探究活動
        </Button>
      </Box>
    </Paper>
  );

  return (
    <div className="index-container">
      <IndexPage_Navbar callback_setActivities={setActivities} />
      <Container maxWidth="lg" className="index-content">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4,
          mt: 2
        }}>
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 600,
            color: '#333333'
          }}>
            我參與的探究活動
          </Typography>
          
          {!loading && activities.length > 0 && (
            <Box className="sort-control">
              <FormControl fullWidth>
                <InputLabel id="sort-select-label">排序</InputLabel>
                <Select
                  labelId="sort-select-label"
                  id="sort-select"
                  value={sortOrder}
                  label="排序"
                  onChange={handleChange}
                  sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E3DFFD',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D1CCF9',
                    },
                  }}
                >
                  <MenuItem value="new_to_old">新到舊</MenuItem>
                  <MenuItem value="old_to_new">舊到新</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              載入中...
            </Typography>
          </Box>
        ) : activities.length === 0 ? (
          <EmptyStateComponent />
        ) : (
          <Grid
            container
            spacing={3}
            className="activities-grid"
          >
            {sortedActivities.map((activity) => (
              <Grid item xs={12} sm={6} md={4} key={activity.id}>
                <ActivityCard activity={activity} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* 信息對話框 */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem',
          fontWeight: 600,
          color: '#333333',
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {dialogContent.title}
          <Button
            onClick={handleDialogClose}
            sx={{ 
              minWidth: 'auto',
              padding: '8px',
              borderRadius: '50%',
              color: '#6c757d'
            }}
          >
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            fontSize: '1rem',
            lineHeight: 1.6,
            color: '#495057',
            whiteSpace: 'pre-line'
          }}>
            {dialogContent.content}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDialogClose} 
            variant="contained"
            sx={{
              backgroundColor: 'var(--surface-color)',
              color: 'var(--text-primary)',
              '&:hover': {
                backgroundColor: 'var(--surface-hover)'
              },
              borderRadius: '50px',
              padding: '10px 24px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            我知道了
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}